
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiCall } from '@/lib/api/api-helpers'

interface Project {
  id: string
  uuid: string
  name: string
  slug: string
  order: number
  userId: string
  createdAt: string
  updatedAt: string
}

interface ProjectWithCanvases extends Project {
  totalCanvases: number
  canvases?: Array<{
    id: string
    name: string
    updatedAt: string
    status: string
  }>
}

interface UseProjectsReturn {
  projects: Project[]
  projectsWithCanvases: ProjectWithCanvases[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  createProject: (name: string) => Promise<Project | null>
  updateProject: (uuid: string, data: { name?: string; slug?: string }) => Promise<Project | null>
  deleteProject: (uuid: string) => Promise<boolean>
  reorderProjects: (projectOrders: Array<{ uuid: string; order: number }>) => Promise<boolean>
}

export function useProjects(): UseProjectsReturn {
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuth()

  // Extract user ID for stable dependency (optimization: avoid callback recreation on user object mutation)
  const userId = user?.id

  // Wrapper around shared apiCall that passes isAuthenticated from hook context
  const callApi = useCallback(
    (endpoint: string, options: RequestInit = {}) => apiCall(endpoint, isAuthenticated, options),
    [isAuthenticated]
  )

  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setProjects([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = await callApi('/api/projects') as { success: boolean; data?: Project[]; error?: string }

      if (response.success && response.data) {
        setProjects(response.data)
      } else {
        throw new Error(response.error || 'Failed to fetch projects')
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch projects')
      setProjects([])
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, userId, callApi])

  const createProject = useCallback(async (name: string): Promise<Project | null> => {
    try {
      setError(null)

      const response = await callApi('/api/projects', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }) as { success: boolean; data?: Project; error?: string }

      if (response.success && response.data) {
        // Refresh projects list
        await fetchProjects()
        return response.data
      } else {
        throw new Error(response.error || 'Failed to create project')
      }
    } catch (err) {
      console.error('Failed to create project:', err)
      setError(err instanceof Error ? err.message : 'Failed to create project')
      return null
    }
  }, [callApi, fetchProjects])

  const updateProject = useCallback(async (uuid: string, data: { name?: string; slug?: string }): Promise<Project | null> => {
    try {
      setError(null)

      const response = await callApi(`/api/projects/${uuid}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }) as { success: boolean; data?: Project; error?: string }

      if (response.success && response.data) {
        // Refresh projects list
        await fetchProjects()
        return response.data
      } else {
        throw new Error(response.error || 'Failed to update project')
      }
    } catch (err) {
      console.error('Failed to update project:', err)
      setError(err instanceof Error ? err.message : 'Failed to update project')
      return null
    }
  }, [callApi, fetchProjects])

  const deleteProject = useCallback(async (uuid: string): Promise<boolean> => {
    try {
      setError(null)

      const response = await callApi(`/api/projects/${uuid}`, {
        method: 'DELETE',
      }) as { success: boolean; error?: string }

      if (response.success) {
        // Refresh projects list
        await fetchProjects()
        return true
      } else {
        throw new Error(response.error || 'Failed to delete project')
      }
    } catch (err) {
      console.error('Failed to delete project:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete project')
      return false
    }
  }, [callApi, fetchProjects])

  const reorderProjects = useCallback(async (projectOrders: Array<{ uuid: string; order: number }>): Promise<boolean> => {
    // Store original projects state for rollback
    let originalProjects: Project[] = []

    try {
      setError(null)

      // Optimistic update: immediately reorder projects in local state using functional update
      setProjects(prev => {
        originalProjects = [...prev]
        return [...prev].sort((a, b) => {
          const orderA = projectOrders.find(p => p.uuid === a.uuid)?.order ?? a.order
          const orderB = projectOrders.find(p => p.uuid === b.uuid)?.order ?? b.order
          return orderA - orderB
        })
      })

      const response = await callApi('/api/projects/reorder', {
        method: 'PUT',
        body: JSON.stringify({ projectOrders }),
      }) as { success: boolean; error?: string }

      if (response.success) {
        // Success: The optimistic update is confirmed
        // No need to fetch again - optimistic update is already correct
        return true
      } else {
        throw new Error(response.error || 'Failed to reorder projects')
      }
    } catch (err) {
      console.error('Failed to reorder projects:', err)

      // Rollback optimistic update on error
      setProjects(originalProjects)

      setError(err instanceof Error ? err.message : 'Failed to reorder projects')
      return false
    }
  }, [callApi])

  // Transform projects to include canvas count (placeholder until canvas API provides counts)
  const projectsWithCanvases: ProjectWithCanvases[] = useMemo(() =>
    projects.map(project => ({
      ...project,
      totalCanvases: 0,
      canvases: [],
    })),
    [projects]
  )

  // Initial fetch
  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  return {
    projects,
    projectsWithCanvases,
    isLoading,
    error,
    refetch: fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    reorderProjects,
  }
}

export type { Project, ProjectWithCanvases }
